// Treats a backgrounded WebView as a soft disconnect.
//
// THIS IS THE ONE THING THE FLUTTER CLIENT DOES NOT NEED. A native app gets
// lifecycle callbacks and keeps its socket; a WebView gets SUSPENDED by the
// OS. Timers stop, the socket's close event may never fire, and on return the
// page believes it is still connected to a match the server removed it from
// twenty seconds ago. Nothing in the Nakama protocol tells us this happened —
// the only signal is `visibilitychange`.
//
// The recovery is a RESYNC FROM SERVER STATE, never a replay of buffered local
// input: opCode 2 carries the whole match state, and the round the player was
// typing in has almost certainly been scored while they were away.
//
// Note the asymmetry with the reconnect loop: hiding does nothing at all
// (backgrounding for two seconds while a notification is dismissed is not a
// disconnect), and only the RETURN triggers work. Acting on `hidden` would
// mean tearing down a live socket every time the player glanced at a message.

export interface VisibilityWatcherOptions {
  /**
   * How long the page must have been hidden before returning counts as a
   * suspension worth resyncing.
   *
   * 3s: below that, the OS has not had time to suspend anything and the socket
   * is provably alive (a suspended page cannot have received the frames it
   * received). Above it, assume the worst and re-read state — a resync costs
   * one round trip and is idempotent, while a missed suspension costs the
   * player the match.
   */
  suspensionThresholdMs?: number;
  /** Called on return from a long-enough absence. */
  onResume: (hiddenForMs: number) => void;
  /** Called when the page is hidden, for analytics only. Must not tear down. */
  onHide?: () => void;
}

/**
 * Starts watching. Returns a stop function.
 *
 * `pagehide`/`pageshow` are watched alongside `visibilitychange` because
 * Android WebViews are inconsistent about which fires: some emit only
 * `pagehide` when the host Activity goes to the background. Both routes feed
 * the same timestamp, and the threshold makes a duplicate harmless.
 */
export function watchVisibility(opts: VisibilityWatcherOptions): () => void {
  const threshold = opts.suspensionThresholdMs ?? 3_000;
  let hiddenAt: number | null = null;

  const hide = (): void => {
    if (hiddenAt !== null) return;
    hiddenAt = Date.now();
    opts.onHide?.();
  };

  const show = (): void => {
    if (hiddenAt === null) return;
    const hiddenForMs = Date.now() - hiddenAt;
    hiddenAt = null;
    if (hiddenForMs >= threshold) opts.onResume(hiddenForMs);
  };

  const onVisibilityChange = (): void => {
    if (document.visibilityState === 'hidden') hide();
    else show();
  };

  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('pagehide', hide);
  window.addEventListener('pageshow', show);
  // A WebView being destroyed sometimes only fires `blur`. Not treated as a
  // hide (a soft keyboard steals focus constantly), which is why it is absent
  // here — noted so nobody adds it as an "improvement".

  return () => {
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('pagehide', hide);
    window.removeEventListener('pageshow', show);
  };
}
